import { Box, CircularProgress } from "@mui/material";
import { CoverageTierEnum, RateDesignEnum } from "@platform/openapi-types";
import { useCallback, useMemo } from "react";

import { useGetRateScheduleStats } from "../../../hooks/useGetRateScheduleStats";
import { useIsVolumeBased } from "../../../hooks/useIsVolumeBased";
import { useUpdateRate, useUpdateProposalRate } from "../../../hooks/useRates";
import { isCriticalIllnessCoverageType } from "../../../utils/rate-helpers";
import {
  formatAgeBandLabel,
  formatRateValue,
  getQuantityLabel,
  hasEmployeeSpouseSplit,
  sortRatesByAgeBand,
} from "../../../utils/rateFormatters";
import { parseIntBaseTen } from "../../../utils/transforms";
import {
  buildRateFieldPath,
  quantityFieldOf,
} from "../../../utils/rateFieldPath";

import { CompanionRatesSection } from "./CompanionRatesSection";
import { EnrollmentFlag } from "./EnrollmentFlag";
import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import {
  RenewalHeaderCell,
  RenewalRowCells,
  RenewalSubHeaderCells,
} from "./RenewalRateCell";
import {
  resolveDestinationPlans,
  useAgeBandedRateCopy,
} from "./useAgeBandedRateCopy";
import {
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  FONT_SIZE,
  SPACE_BETWEEN,
  gridCellSx,
  gridRowSx,
  headerCellSx,
  inputSx,
} from "./constants";

import type { RateCopyColumn } from "./useAgeBandedRateCopy";
import type { ColDef } from "ag-grid-community";
import type { AxiosInstance } from "axios";
import type { FC } from "react";
import type { Rate, RateSchedule } from "../../../types";
import type { RateCellField } from "../../../utils/rateFieldPath";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";

type MutableRateField = "numberOfLives" | "rate" | "volume";

const buildNumericRatePatch = (
  value: string,
  parser: (value: string) => number,
  field: MutableRateField,
  currentValue: number | null | undefined,
): { [key: string]: number | null } | null => {
  const trimmedValue = value.trim();

  if (trimmedValue === "") {
    return currentValue == null ? null : { [field]: null };
  }

  const numericValue = parser(trimmedValue);
  if (Number.isNaN(numericValue) || numericValue === currentValue) {
    return null;
  }

  return { [field]: numericValue };
};

export interface AgeBandedRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  /** Coverage type (e.g., dental, vision, std) for LOC-specific behavior */
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
  benefitPlanGroup?: BenefitPlanGroup;
  /**
   * All plans in the coverage offered as copy destinations. Spans every
   * benefit plan group so rates can be copied across groups (e.g. std-1-1 to
   * std-2-1), not just within the current group.
   */
  copyDestinationPlans?: BenefitPlan[];
}

/**
 * Build column definitions for age banded rate design in RatesGrid
 * Creates columns for each age band
 */
export const buildAgeBandedColumns = (_group: BenefitPlanGroup): ColDef[] =>
  // TODO: Extract unique age bands from rate schedules
  // and create a column for each age band
  [];

/**
 * Build column definitions for age banded rate design in SummaryGrid
 * Returns array of ColDef for sub-columns within a benefit plan
 */
export const buildAgeBandedSummaryColumns = (
  _planId: string,
  _planName: string,
): ColDef[] =>
  // TODO: Implement age banded summary column generation
  [];

const AG_HEADER_HEIGHT = "36px";

// Local grid layouts
// CSS Grid container styling - 3 columns: AgeBand (fixed), Enrolled Lives (flex), Rate (flex)
// Uses flexible fr units to expand to full column width
const gridContainerSx = {
  display: "grid",
  gridTemplateColumns: "minmax(100px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr)",
  width: "100%",
};

// With a renewal column appended, the standard table gains a 4th track and scrolls within
// the pinned benefit-plan cell (content-min tracks instead of flex so it can overflow).
const standardGridSx = (showRenewal: boolean): Record<string, unknown> =>
  showRenewal
    ? {
        display: "grid",
        gridTemplateColumns: "minmax(100px, 1fr) repeat(3, minmax(70px, 1fr))",
        width: "100%",
        overflowX: "auto",
      }
    : gridContainerSx;

// CSS Grid container styling - 5 columns: AgeBand, EE Volume, SP Volume, EE Rate, SP Rate (rates differ)
const gridContainerEmpSpSx = {
  display: "grid",
  gridTemplateColumns:
    "minmax(100px, 1fr) minmax(60px, 1fr) minmax(60px, 1fr) minmax(60px, 1fr) minmax(60px, 1fr)",
  width: "100%",
};

// With renewal shown, the differing-rate layout gains an EE + SP renewal column (6 data
// tracks) and scrolls within the pinned cell (content-min tracks so it can overflow).
const empSpDifferGridSx = (showRenewal: boolean): Record<string, unknown> =>
  showRenewal
    ? {
        display: "grid",
        gridTemplateColumns: "minmax(100px, 1fr) repeat(6, minmax(76px, 1fr))",
        width: "100%",
        overflowX: "auto",
      }
    : gridContainerEmpSpSx;

// CSS Grid container styling - 4 columns: AgeBand, EE Volume, SP Volume, Rate (rates same)
const gridContainerEmpSpSameRateSx = {
  display: "grid",
  gridTemplateColumns:
    "minmax(100px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr)",
  width: "100%",
};

// With renewal shown, the shared-rate layout gains one renewal column (4 data tracks) and
// scrolls within the pinned cell.
const empSpSameRateGridSx = (showRenewal: boolean): Record<string, unknown> =>
  showRenewal
    ? {
        display: "grid",
        gridTemplateColumns: "minmax(100px, 1fr) repeat(4, minmax(76px, 1fr))",
        width: "100%",
        overflowX: "auto",
      }
    : gridContainerEmpSpSameRateSx;

/**
 * Renderer for age banded rate design
 *
 * Displays rates by age band per benefit plan in the group.
 * Each age band has its own row with numberOfLives and rate inputs.
 *
 * Layout:
 * | Rate Structure |                | Age Banded (X bands) |
 * | Age Band       | Enrolled Lives | Rate                 |
 * | 0-17           | [input]        | [input]              |
 * | 18-24          | [input]        | [input]              |
 * ...
 */
// This renderer already carries three layout variants (standard, EE/SP same-rate, EE/SP
// differing-rate) and sits over the complexity limit on its own; the renewal column adds
// one more branch. Splitting the variants into their own components is the real fix and is
// tracked with the EE/SP renewal work — out of scope for adding the column here.
/* eslint-disable complexity */
export const AgeBandedRenderer: FC<AgeBandedRendererProps> = ({
  plan,
  axiosInstance,
  coverageType,
  onRateSaved,
  readOnly,
  benefitPlanGroup,
  copyDestinationPlans,
}) => {
  const {
    rateSchedule,
    companionRateSchedule,
    spouseAgeBandedSchedule,
    rates,
    companionRates,
    renewal,
    isLoading,
  } = useGetRateScheduleStats(plan);
  const showRenewal = renewal.showRenewal;

  // Mutation hook for updating rates
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  // Determine if rates use volume-based units (volumePer*, per*OfSalary)
  const isVolumeBased = useIsVolumeBased(rates);
  const quantityFieldName = quantityFieldOf(isVolumeBased);

  const isCriticalIllness = isCriticalIllnessCoverageType(coverageType);

  // Quantity column label adapts to unit type
  const quantityLabel = isVolumeBased
    ? getQuantityLabel(rates[0]?.unit, false, coverageType)
    : "Vol";

  const eeQuantityLabel = "EE Volume";
  const spQuantityLabel = "SP Volume";

  // Map each rate id to its parent schedule id so an edit always PATCHes the
  // schedule that actually holds the rate. Carriers like Unum split EE and SP
  // into separate ageBanded schedules; routing by tier alone would misroute a
  // spouseOnly rate that lives in the primary schedule.
  const scheduleByRateId = useMemo(
    () =>
      new Map(
        [rateSchedule, spouseAgeBandedSchedule].flatMap((s) =>
          s?.id == null
            ? []
            : (s.rates ?? [])
                .filter((r) => r.id != null)
                .map((r): [string, RateSchedule] => [r.id!, s]),
        ),
      ),
    [rateSchedule, spouseAgeBandedSchedule],
  );

  const resolveSchedule = useCallback(
    (rate: Rate): RateSchedule | undefined =>
      (rate.id ? scheduleByRateId.get(rate.id) : undefined) ?? rateSchedule,
    [scheduleByRateId, rateSchedule],
  );

  const resolveScheduleId = useCallback(
    (rate: Rate): string | undefined => resolveSchedule(rate)?.id,
    [resolveSchedule],
  );

  /** Keys a cell against the schedule the rate actually lives in. */
  const fieldPathOf = useCallback(
    (rate: Rate | undefined, field: RateCellField): string | undefined => {
      const schedule = rate && resolveSchedule(rate);
      return rate && schedule
        ? buildRateFieldPath(schedule, rate, field)
        : undefined;
    },
    [resolveSchedule],
  );

  const handleCommit =
    (parser: (val: string) => number, rateField: MutableRateField) =>
    (rate: Rate, value: string): void => {
      const scheduleId = resolveScheduleId(rate);
      if (!axiosInstance || !scheduleId || !rate?.id) return;

      const ratePatch = buildNumericRatePatch(
        value,
        parser,
        rateField,
        rate[rateField] as number | null | undefined,
      );
      if (!ratePatch) return;

      rateMutation.mutate(
        {
          scheduleId,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: ratePatch,
        },
        {
          onSuccess: () => onRateSaved?.(),
        },
      );
    };

  // Handle commit for numberOfLives (unit-based rates)
  const handleLivesCommit = handleCommit(parseIntBaseTen, "numberOfLives");

  // Handle commit for volume (volume-based rates)
  const handleVolumeCommit = handleCommit(parseFloat, "volume");

  // Unified quantity commit handler based on unit type
  const handleQuantityCommit = isVolumeBased
    ? handleVolumeCommit
    : handleLivesCommit;

  // Get display value for quantity field
  const getQuantityValue = useCallback(
    (rate: Rate): string => {
      if (isVolumeBased) {
        return rate.volume != null ? String(rate.volume) : "";
      }
      return rate.numberOfLives != null ? String(rate.numberOfLives) : "";
    },
    [isVolumeBased],
  );

  // Handle commit for rate
  const handleRateCommit = handleCommit(parseFloat, "rate");

  // All rates in the primary schedule are age-banded (no flat companion rates mixed in)
  const ageBandedRates: Rate[] = useMemo(
    () => sortRatesByAgeBand(rates),
    [rates],
  );

  // Determine if we have EE/SP split by checking for an actual spouse rate —
  // either in the primary schedule, or in the dedicated spouse schedule some
  // carriers (Unum) send instead. resolveSpouseAgeBandedSchedule only ever
  // returns a schedule whose rates are all spouseOnly, so its presence alone
  // is real split evidence, same as a spouseOnly rate in the primary schedule.
  const hasEmpSpouseSplit = useMemo(
    () =>
      hasEmployeeSpouseSplit(ageBandedRates) ||
      (spouseAgeBandedSchedule?.rates?.length ?? 0) > 0,
    [ageBandedRates, spouseAgeBandedSchedule],
  );

  // Group rates by age band for EE/SP layout
  const ratesByAgeBand = useMemo(() => {
    if (!hasEmpSpouseSplit) return null;

    const grouped: Record<string, { eeRate?: Rate; spRate?: Rate }> = {};

    ageBandedRates.forEach((rate) => {
      const key = `${rate.ageBandLower}-${rate.ageBandUpper}`;
      if (!grouped[key]) {
        grouped[key] = {};
      }
      if (rate.coverageTierCode === CoverageTierEnum.employee) {
        grouped[key].eeRate = rate;
      } else if (rate.coverageTierCode === CoverageTierEnum.spouseOnly) {
        grouped[key].spRate = rate;
      }
    });

    // Some carriers (Unum) send EE and SP as two separate ageBanded schedules.
    // Pull SP rates from the dedicated spouse schedule when the primary has none.
    if (spouseAgeBandedSchedule) {
      sortRatesByAgeBand(spouseAgeBandedSchedule.rates ?? []).forEach(
        (rate) => {
          const key = `${rate.ageBandLower}-${rate.ageBandUpper}`;
          if (!grouped[key]) grouped[key] = {};
          grouped[key].spRate = rate;
        },
      );
    }

    return grouped;
  }, [ageBandedRates, hasEmpSpouseSplit, spouseAgeBandedSchedule]);

  // True when "EMP & Spouse Rates Differ" is No — triggers the 4-column same-rate
  // layout. Prefers the persisted flag (set explicitly by the RatesDrawer toggle,
  // since it can't be reconstructed from rate values alone — a fresh "Yes" and an
  // unedited "No" both start out with identical EE/SP amounts). Falls back to
  // comparing rate values for schedules saved before the flag was persisted.
  const hasEmpSpouseSameRate = useMemo(() => {
    if (!hasEmpSpouseSplit || !ratesByAgeBand) return false;
    if (rateSchedule?.empSpouseRatesDiffer) {
      return rateSchedule.empSpouseRatesDiffer === "no";
    }
    return Object.values(ratesByAgeBand).every(
      ({ eeRate, spRate }) => eeRate?.rate === spRate?.rate,
    );
  }, [hasEmpSpouseSplit, ratesByAgeBand, rateSchedule]);

  // Get unique age bands in order (from age-banded rates only)
  const ageBands = useMemo(() => {
    const seen = new Set<string>();
    const bands: { lower?: number; upper?: number; key: string }[] = [];
    ageBandedRates.forEach((rate) => {
      const key = `${rate.ageBandLower}-${rate.ageBandUpper}`;
      if (!seen.has(key)) {
        seen.add(key);
        bands.push({
          lower: rate.ageBandLower,
          upper: rate.ageBandUpper,
          key,
        });
      }
    });
    return bands;
  }, [ageBandedRates]);

  // Plans offered as copy destinations: every plan in the coverage (across all
  // groups) plus this plan itself, so a column can be copied onto any sibling
  // column. Falls back to the current group when the coverage-wide list isn't
  // provided. Alternate plans have no rates, so they're excluded.
  const copyColumns: RateCopyColumn[] = useMemo(
    () => [
      {
        id: "ee",
        label: "EE",
        matches: (rate: Rate) =>
          rate.coverageTierCode === CoverageTierEnum.employee,
      },
      {
        id: "sp",
        label: "SP",
        matches: (rate: Rate) =>
          rate.coverageTierCode === CoverageTierEnum.spouseOnly,
      },
    ],
    [],
  );

  const destinationPlans = useMemo(
    () => resolveDestinationPlans(plan, copyDestinationPlans, benefitPlanGroup),
    [plan, copyDestinationPlans, benefitPlanGroup],
  );

  const { renderCopyButton, copyDialog } = useAgeBandedRateCopy({
    plan,
    rates,
    rateSchedule,
    columns: copyColumns,
    rateDesign: RateDesignEnum.ageBanded,
    destinationPlans,
    updateRateMutation,
    updateProposalRateMutation,
    onRateSaved,
  });

  const gridId = `age-banded-${plan.id}`;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rateSchedule) {
    return (
      <Box sx={{ p: 1, fontSize: "13px", color: "text.secondary" }}>
        No rate schedule
      </Box>
    );
  }

  // EE/SP Same-Rate Layout ("Differ? = No"): EE Volume | SP Volume | Rate
  if (hasEmpSpouseSplit && ratesByAgeBand && hasEmpSpouseSameRate) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: SPACE_BETWEEN,
            alignItems: "center",
            height: AG_HEADER_HEIGHT,
            backgroundColor: AG_HEADER_BG_COLOR,
            borderBottom: AG_BORDER,
            px: 1,
          }}
        >
          <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>
            Age Banded ({ageBands.length} bands)
          </Box>
        </Box>

        <Box sx={empSpSameRateGridSx(showRenewal)}>
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }}>Age Band</Box>
            <Box sx={{ ...headerCellSx }}>EE Volume</Box>
            <Box sx={{ ...headerCellSx }}>SP Volume</Box>
            <Box sx={{ ...headerCellSx, borderRight: "none" }}>
              {showRenewal ? "Current" : "Rate"}
            </Box>
            <RenewalHeaderCell show={showRenewal} label={renewal.columnLabel} />
          </Box>

          {ageBands.map((band, index) => {
            const { eeRate, spRate } = ratesByAgeBand[band.key] || {};
            const ageBandLabel = formatAgeBandLabel(band.lower, band.upper, {
              isHighestBand: index === ageBands.length - 1,
              isCriticalIllness,
            });
            const isLastRow =
              index === ageBands.length - 1 && companionRates.length === 0;
            const colsPerBand = 3; // EE Volume, SP Volume, Rate
            const eeQtyIndex = index * colsPerBand;
            const spQtyIndex = index * colsPerBand + 1;
            const rateIndex = index * colsPerBand + 2;

            return (
              <Box key={band.key} sx={gridRowSx}>
                <Box
                  sx={{
                    ...headerCellSx,
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {ageBandLabel}
                </Box>
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={fieldPathOf(eeRate, quantityFieldName)}
                  sx={{
                    ...gridCellSx,
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {eeRate && (
                    <EnrollmentFlag
                      rate={eeRate}
                      measure={isVolumeBased ? "volume" : "lives"}
                    >
                      <DebouncedNumericInput
                        serverValue={getQuantityValue(eeRate)}
                        onCommit={(value) =>
                          handleQuantityCommit(eeRate, value)
                        }
                        allowDecimals={isVolumeBased}
                        isCurrency={isVolumeBased}
                        sx={inputSx}
                        gridId={gridId}
                        cellIndex={eeQtyIndex}
                        columnsInRow={colsPerBand}
                        readOnly={readOnly}
                        inputProps={{
                          "aria-label": `${ageBandLabel} EE Volume`,
                          tabIndex: 0,
                        }}
                      />
                    </EnrollmentFlag>
                  )}
                </RateReviewCell>
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={fieldPathOf(spRate, quantityFieldName)}
                  sx={{
                    ...gridCellSx,
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {spRate && (
                    <EnrollmentFlag
                      rate={spRate}
                      measure={isVolumeBased ? "volume" : "lives"}
                    >
                      <DebouncedNumericInput
                        serverValue={getQuantityValue(spRate)}
                        onCommit={(value) =>
                          handleQuantityCommit(spRate, value)
                        }
                        allowDecimals={isVolumeBased}
                        isCurrency={isVolumeBased}
                        sx={inputSx}
                        gridId={gridId}
                        cellIndex={spQtyIndex}
                        columnsInRow={colsPerBand}
                        readOnly={readOnly}
                        inputProps={{
                          "aria-label": `${ageBandLabel} SP Volume`,
                          tabIndex: 0,
                        }}
                      />
                    </EnrollmentFlag>
                  )}
                </RateReviewCell>
                {/* Shared Rate input — commits to both EE and SP since rates are the same */}
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={fieldPathOf(eeRate, "rate")}
                  sx={{
                    ...gridCellSx,
                    borderRight: "none",
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {eeRate && (
                    <DebouncedNumericInput
                      serverValue={formatRateValue(eeRate.rate, rates[0]?.unit)}
                      onCommit={(value) => {
                        handleRateCommit(eeRate, value);
                        if (spRate) handleRateCommit(spRate, value);
                      }}
                      allowDecimals
                      isCurrency
                      sx={inputSx}
                      gridId={gridId}
                      cellIndex={rateIndex}
                      columnsInRow={colsPerBand}
                      readOnly={readOnly}
                      inputProps={{
                        "aria-label": `${ageBandLabel} Rate`,
                        tabIndex: 0,
                      }}
                    />
                  )}
                </RateReviewCell>
                <RenewalRowCells
                  show={showRenewal}
                  renewal={renewal}
                  rowLabelPrefix={ageBandLabel}
                  isLastRow={isLastRow}
                  cells={[{ rate: eeRate, label: "" }]}
                />
              </Box>
            );
          })}

          {companionRates.length > 0 && companionRateSchedule && (
            <CompanionRatesSection
              companionRates={companionRates}
              companionRateSchedule={companionRateSchedule}
              planId={plan.id}
              isProposalRateSchedule={plan.rateScheduleSource === "proposal"}
              totalColumns={showRenewal ? 5 : 4}
              renewalColumns={Number(showRenewal) * 1}
              volumeSpan={2}
              rateSpan={1}
              onRateSaved={onRateSaved}
            />
          )}
        </Box>
      </Box>
    );
  }

  // EE/SP Split Layout ("Differ? = Yes"): EE Volume | SP Volume | EE Rate | SP Rate
  if (hasEmpSpouseSplit && ratesByAgeBand) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header Row - Rate Design Label */}
        <Box
          sx={{
            display: "flex",
            justifyContent: SPACE_BETWEEN,
            alignItems: "center",
            height: AG_HEADER_HEIGHT,
            backgroundColor: AG_HEADER_BG_COLOR,
            borderBottom: AG_BORDER,
            px: 1,
          }}
        >
          <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>
            Age Banded ({ageBands.length} bands)
          </Box>
        </Box>

        <Box sx={empSpDifferGridSx(showRenewal)}>
          {/* Sub-header Row - Column Labels */}
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }}>Age Band</Box>
            <Box sx={{ ...headerCellSx }}>{eeQuantityLabel}</Box>
            <Box sx={{ ...headerCellSx }}>{spQuantityLabel}</Box>
            <Box
              sx={{
                ...headerCellSx,
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>EE Rate</span>
              {!readOnly && renderCopyButton("ee", "EE")}
            </Box>
            <Box
              sx={{
                ...headerCellSx,
                borderRight: "none",
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>SP Rate</span>
              {!readOnly && renderCopyButton("sp", "SP")}
            </Box>
            <RenewalSubHeaderCells
              show={showRenewal}
              labels={[
                `${renewal.columnLabel} EE`,
                `${renewal.columnLabel} SP`,
              ]}
            />
          </Box>

          {/* Rate Rows - One per age band with EE and SP columns */}
          {ageBands.map((band, index) => {
            const { eeRate, spRate } = ratesByAgeBand[band.key] || {};
            const ageBandLabel = formatAgeBandLabel(band.lower, band.upper, {
              isHighestBand: index === ageBands.length - 1,
              isCriticalIllness,
            });
            const isLastRow =
              index === ageBands.length - 1 && companionRates.length === 0;
            const colsPerBand = 4; // EE Quantity, SP Quantity, EE Rate, SP Rate
            const eeQtyIndex = index * colsPerBand;
            const spQtyIndex = index * colsPerBand + 1;
            const eeRateIndex = index * colsPerBand + 2;
            const spRateIndex = index * colsPerBand + 3;

            return (
              <Box key={band.key} sx={gridRowSx}>
                {/* Age Band Label */}
                <Box
                  sx={{
                    ...headerCellSx,
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {ageBandLabel}
                </Box>
                {/* EE Quantity Input */}
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={fieldPathOf(eeRate, quantityFieldName)}
                  sx={{
                    ...gridCellSx,
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {eeRate && (
                    <EnrollmentFlag
                      rate={eeRate}
                      measure={isVolumeBased ? "volume" : "lives"}
                    >
                      <DebouncedNumericInput
                        serverValue={getQuantityValue(eeRate)}
                        onCommit={(value) =>
                          handleQuantityCommit(eeRate, value)
                        }
                        allowDecimals={isVolumeBased}
                        isCurrency={isVolumeBased}
                        sx={inputSx}
                        gridId={gridId}
                        cellIndex={eeQtyIndex}
                        columnsInRow={colsPerBand}
                        readOnly={readOnly}
                        inputProps={{
                          "aria-label": `${ageBandLabel} ${eeQuantityLabel}`,
                          tabIndex: 0,
                        }}
                      />
                    </EnrollmentFlag>
                  )}
                </RateReviewCell>
                {/* SP Quantity Input */}
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={fieldPathOf(spRate, quantityFieldName)}
                  sx={{
                    ...gridCellSx,
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {spRate && (
                    <EnrollmentFlag
                      rate={spRate}
                      measure={isVolumeBased ? "volume" : "lives"}
                    >
                      <DebouncedNumericInput
                        serverValue={getQuantityValue(spRate)}
                        onCommit={(value) =>
                          handleQuantityCommit(spRate, value)
                        }
                        allowDecimals={isVolumeBased}
                        isCurrency={isVolumeBased}
                        sx={inputSx}
                        gridId={gridId}
                        cellIndex={spQtyIndex}
                        columnsInRow={colsPerBand}
                        readOnly={readOnly}
                        inputProps={{
                          "aria-label": `${ageBandLabel} ${spQuantityLabel}`,
                          tabIndex: 0,
                        }}
                      />
                    </EnrollmentFlag>
                  )}
                </RateReviewCell>
                {/* EE Rate Input */}
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={fieldPathOf(eeRate, "rate")}
                  sx={{
                    ...gridCellSx,
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {eeRate && (
                    <DebouncedNumericInput
                      serverValue={formatRateValue(eeRate.rate, rates[0]?.unit)}
                      onCommit={(value) => handleRateCommit(eeRate, value)}
                      allowDecimals
                      isCurrency
                      sx={inputSx}
                      gridId={gridId}
                      cellIndex={eeRateIndex}
                      columnsInRow={colsPerBand}
                      readOnly={readOnly}
                      inputProps={{
                        "aria-label": `${ageBandLabel} EE Rate`,
                        tabIndex: 0,
                      }}
                    />
                  )}
                </RateReviewCell>
                {/* SP Rate Input */}
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={fieldPathOf(spRate, "rate")}
                  sx={{
                    ...gridCellSx,
                    borderRight: "none",
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {spRate && (
                    <DebouncedNumericInput
                      serverValue={formatRateValue(spRate.rate, rates[0]?.unit)}
                      onCommit={(value) => handleRateCommit(spRate, value)}
                      allowDecimals
                      isCurrency
                      sx={inputSx}
                      gridId={gridId}
                      cellIndex={spRateIndex}
                      columnsInRow={colsPerBand}
                      readOnly={readOnly}
                      inputProps={{
                        "aria-label": `${ageBandLabel} SP Rate`,
                        tabIndex: 0,
                      }}
                    />
                  )}
                </RateReviewCell>
                <RenewalRowCells
                  show={showRenewal}
                  renewal={renewal}
                  rowLabelPrefix={ageBandLabel}
                  isLastRow={isLastRow}
                  cells={[
                    { rate: eeRate, label: "EE" },
                    { rate: spRate, label: "SP" },
                  ]}
                />
              </Box>
            );
          })}

          {/* Companion Rate Section (companion flat schedule) */}
          {companionRates.length > 0 && companionRateSchedule && (
            <CompanionRatesSection
              companionRates={companionRates}
              companionRateSchedule={companionRateSchedule}
              planId={plan.id}
              isProposalRateSchedule={plan.rateScheduleSource === "proposal"}
              totalColumns={showRenewal ? 7 : 5}
              renewalColumns={Number(showRenewal) * 2}
              onRateSaved={onRateSaved}
            />
          )}
        </Box>

        {copyDialog}
      </Box>
    );
  }

  // Standard Layout (no EE/SP split)
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        zIndex: 1,
      }}
    >
      {/* Header Row - Rate Design Label */}
      <Box
        sx={{
          display: "flex",
          justifyContent: SPACE_BETWEEN,
          alignItems: "center",
          height: AG_HEADER_HEIGHT,
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          px: 1,
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>
          Age Banded ({ageBands.length} bands)
        </Box>
      </Box>

      <Box sx={standardGridSx(showRenewal)}>
        {/* Sub-header Row - Column Labels */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>Age Band</Box>
          <Box sx={{ ...headerCellSx }}>{quantityLabel}</Box>
          <Box sx={{ ...headerCellSx, borderRight: "none" }}>
            {showRenewal ? "Current" : "Rate"}
          </Box>
          <RenewalHeaderCell show={showRenewal} label={renewal.columnLabel} />
        </Box>

        {/* Rate Rows - One per age band */}
        {ageBandedRates.map((rate, index) => {
          const ageBandLabel = formatAgeBandLabel(
            rate.ageBandLower,
            rate.ageBandUpper,
            {
              isHighestBand: index === ageBandedRates.length - 1,
              isCriticalIllness,
            },
          );
          const isLastRow =
            index === ageBandedRates.length - 1 && companionRates.length === 0;
          const colsPerBand = 2; // Quantity, Rate
          const qtyIndex = index * colsPerBand;
          const rateIndex = index * colsPerBand + 1;
          return (
            <Box key={rate.id} sx={gridRowSx}>
              {/* Age Band Label */}
              <Box
                sx={{
                  ...headerCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {ageBandLabel}
              </Box>
              {/* Quantity Input */}
              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(rate, quantityFieldName)}
                sx={{
                  ...gridCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                <EnrollmentFlag
                  rate={rate}
                  measure={isVolumeBased ? "volume" : "lives"}
                >
                  <DebouncedNumericInput
                    serverValue={getQuantityValue(rate)}
                    onCommit={(value) => handleQuantityCommit(rate, value)}
                    allowDecimals={isVolumeBased}
                    isCurrency={isVolumeBased}
                    sx={inputSx}
                    gridId={gridId}
                    cellIndex={qtyIndex}
                    columnsInRow={colsPerBand}
                    readOnly={readOnly}
                    inputProps={{
                      "aria-label": `${ageBandLabel} ${quantityLabel}`,
                      tabIndex: 0,
                    }}
                  />
                </EnrollmentFlag>
              </RateReviewCell>
              {/* Rate Input */}
              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(rate, "rate")}
                sx={{
                  ...gridCellSx,
                  borderRight: "none",
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                <DebouncedNumericInput
                  serverValue={formatRateValue(rate.rate, rates[0]?.unit)}
                  onCommit={(value) => handleRateCommit(rate, value)}
                  allowDecimals
                  isCurrency
                  sx={inputSx}
                  gridId={gridId}
                  cellIndex={rateIndex}
                  columnsInRow={colsPerBand}
                  readOnly={readOnly}
                  inputProps={{
                    "aria-label": `${ageBandLabel} Rate`,
                    tabIndex: 0,
                  }}
                />
              </RateReviewCell>
              <RenewalRowCells
                show={showRenewal}
                renewal={renewal}
                rowLabelPrefix={ageBandLabel}
                isLastRow={isLastRow}
                cells={[{ rate, label: "" }]}
              />
            </Box>
          );
        })}

        {/* Companion Rate Section (companion flat schedule) */}
        {companionRates.length > 0 && companionRateSchedule && (
          <CompanionRatesSection
            companionRates={companionRates}
            companionRateSchedule={companionRateSchedule}
            planId={plan.id}
            isProposalRateSchedule={plan.rateScheduleSource === "proposal"}
            totalColumns={showRenewal ? 4 : 3}
            renewalColumns={Number(showRenewal) * 1}
            onRateSaved={onRateSaved}
          />
        )}
      </Box>
    </Box>
  );
};
/* eslint-enable complexity */
